import { useState, useEffect } from "react";
import { Typography, Button, Table, Modal, Input, Select, Space, message, Popconfirm, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, BankOutlined } from "@ant-design/icons";
import { api } from "../../services/api";
import { useClient } from "../../context/ClientContext";
import { useAuth } from "../../context/AuthContext";
import { SkStyleTag, skeletonColumns, skeletonRows } from "../../shared/components/TableSkeleton";
import { hexAlpha } from "../../utils/colorUtils";

const { Title, Text } = Typography;

const CircleBtn = ({ icon, onClick, title, danger, disabled }) => (
  <Tooltip title={title}>
    <button type="button" onClick={onClick} disabled={disabled} style={{
      width: 32, height: 32, border: `1px solid ${danger ? '#fca5a5' : '#e5e7eb'}`,
      borderRadius: '50%', background: '#fff', display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer',
      color: danger ? '#ef4444' : '#374151', fontSize: 14, flexShrink: 0,
      opacity: disabled ? 0.45 : 1, transition: 'border-color 0.15s',
    }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.borderColor = danger ? '#ef4444' : '#374151'; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.borderColor = danger ? '#fca5a5' : '#e5e7eb'; }}
    >{icon}</button>
  </Tooltip>
);

// id_tesorero 0 = la cuenta es de la tienda.
const EMPTY = { nombre: "", entidad: "", banco: "", cbu_cvu: "", alias: "", titular: "", cuit: "", id_tesorero: 0 };

export const CuentasTransferencia = () => {
  const { theme } = useClient();
  const primary = theme?.primaryColor || '#0f172a';
  const primaryText = theme?.primaryTextColor || '#fff';
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, cuenta: null });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  // El admin ve todas y les asigna tesorero; el tesorero ve solo las suyas.
  const esAdmin = user?.id_rol === 1;
  const [tesoreros, setTesoreros] = useState([]);

  const cargar = async () => {
    setLoading(true);
    const res = await api.admin.getCuentasTransferencia();
    if (res.status === "success") setCuentas(res.data || []);
    setLoading(false);
  };

  useEffect(() => { cargar(); }, []);

  useEffect(() => {
    if (!esAdmin) return;
    api.admin.tesoreros().then((res) => { if (res.status === "success") setTesoreros(res.data || []); });
  }, [esAdmin]);

  const abrirCrear = () => {
    setForm(EMPTY);
    setModal({ open: true, cuenta: null });
  };

  const abrirEditar = (cuenta) => {
    setForm({
      nombre: cuenta.nombre || "",
      entidad: cuenta.entidad || "",
      banco: cuenta.banco || "",
      cbu_cvu: cuenta.cbu_cvu || "",
      alias: cuenta.alias || "",
      titular: cuenta.titular || "",
      cuit: cuenta.cuit || "",
      id_tesorero: cuenta.id_tesorero || 0,
    });
    setModal({ open: true, cuenta });
  };

  const guardar = async () => {
    if (!form.nombre.trim()) {
      message.warning("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    const res = modal.cuenta
      ? await api.admin.editarCuenta(modal.cuenta.id, form)
      : await api.admin.crearCuenta(form);
    setSaving(false);

    if (res.status === "success") {
      message.success(modal.cuenta ? "Cuenta actualizada" : "Cuenta creada");
      setModal({ open: false, cuenta: null });
      cargar();
    } else {
      message.error(res.message || "Error al guardar");
    }
  };

  const toggle = async (id) => {
    const res = await api.admin.toggleCuenta(id);
    if (res.status === "success") {
      message.success(res.message);
      cargar();
    }
  };

  const eliminar = async (id) => {
    const res = await api.admin.eliminarCuenta(id);
    if (res.status === "success") {
      message.success("Cuenta eliminada");
      cargar();
    }
  };

  const columns = [
    {
      title: "Cuenta", key: "nombre", minWidth: 220,
      render: (_, c) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 8, flexShrink: 0,
            background: hexAlpha(primary, 0.10),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: primary, fontSize: 18,
          }}><BankOutlined /></div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: primary }}>{c.nombre}</div>
            {(c.entidad || c.banco) && <div style={{ fontSize: 12, color: '#9ca3af' }}>{c.entidad || c.banco}</div>}
          </div>
        </div>
      ),
    },
    ...(esAdmin ? [{
      title: "Tesorero", key: "tesorero", width: 170,
      render: (_, c) => c.tesorero
        ? <Text style={{ fontSize: 13 }}>{c.tesorero}</Text>
        : <Text type="secondary" style={{ fontSize: 13 }}>De la tienda</Text>,
    }] : []),
    {
      title: "Datos", key: "datos",
      render: (_, c) => (
        <div style={{ fontSize: 13, lineHeight: 1.8 }}>
          {c.alias && <div><Text type="secondary" style={{ fontSize: 12 }}>Alias: </Text><Text code style={{ fontSize: 12 }}>{c.alias}</Text></div>}
          {c.cbu_cvu && <div><Text type="secondary" style={{ fontSize: 12 }}>CBU/CVU: </Text><Text code style={{ fontSize: 12 }}>{c.cbu_cvu}</Text></div>}
          {c.titular && <div><Text type="secondary" style={{ fontSize: 12 }}>Titular: </Text><Text style={{ fontSize: 12 }}>{c.titular}</Text></div>}
          {c.cuit && <div><Text type="secondary" style={{ fontSize: 12 }}>CUIT: </Text><Text style={{ fontSize: 12 }}>{c.cuit}</Text></div>}
        </div>
      ),
    },
    {
      title: "Estado", dataIndex: "activa", key: "activa", width: 110,
      render: (v, record) => (
        <Tooltip title={esAdmin ? (v ? "Click para desactivar" : "Click para activar") : null}>
          <span
            onClick={esAdmin ? () => toggle(record.id) : undefined}
            style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '3px 10px', borderRadius: 20,
              border: `1.5px solid ${v ? '#16a34a' : '#9ca3af'}`,
              background: v ? '#f0fdf4' : '#f9fafb',
              color: v ? '#16a34a' : '#9ca3af',
              fontSize: 12, fontWeight: 600,
              cursor: esAdmin ? 'pointer' : 'default', userSelect: 'none',
              whiteSpace: 'nowrap', transition: 'opacity 0.15s',
            }}
          >{v ? 'Activa' : 'Inactiva'}</span>
        </Tooltip>
      ),
    },
    // El tesorero no carga ni edita cuentas: a dónde va la plata lo decide el dueño.
    ...(!esAdmin ? [] : [{
      title: "", key: "acciones", width: 80, align: "center",
      render: (_, record) => (
        <Space size={6}>
          <CircleBtn icon={<EditOutlined />} title="Editar" onClick={() => abrirEditar(record)} />
          <Popconfirm title="¿Eliminar esta cuenta?" onConfirm={() => eliminar(record.id)} okText="Sí" cancelText="No">
            <CircleBtn icon={<DeleteOutlined />} title="Eliminar" danger />
          </Popconfirm>
        </Space>
      ),
    }]),
  ];

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
        <Text strong style={{ fontSize: 28, lineHeight: 1.2 }}>Cuentas de transferencia</Text>
        {esAdmin && (
          <Button
            type="primary" icon={<PlusOutlined />} onClick={abrirCrear}
            style={{ background: primary, borderColor: primary, color: primaryText }}
          >
            Nueva cuenta
          </Button>
        )}
      </div>

      <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 20 }}>
        {esAdmin
          ? "Cargá las cuentas bancarias a las que tus clientes pueden transferir. Si una la maneja un tesorero, asignásela: ve en Cobros lo que entra a esa cuenta, lo aprueba o lo rechaza, y en el reporte se ve cuánto le entró."
          : "Las cuentas que tenés asignadas. Las carga y las modifica el dueño de la tienda: si hay que cambiar algún dato, pedíselo a él."}
      </Text>

      <SkStyleTag />
      <Table
        dataSource={loading ? skeletonRows(6) : cuentas}
        columns={loading ? skeletonColumns(columns, { avatarIndex: 0, twoLineIndexes: [0] }) : columns}
        rowKey={loading ? 'key' : 'id'}
        pagination={false}
        size="middle"
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 8 }}
        locale={{ emptyText: 'No hay cuentas de transferencia cargadas.' }}
      />

      <Modal
        open={modal.open}
        onCancel={() => setModal({ open: false, cuenta: null })}
        title={modal.cuenta ? "Editar cuenta" : "Nueva cuenta"}
        onOk={guardar}
        confirmLoading={saving}
        okText="Guardar"
        cancelText="Cancelar"
        width={500}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>Nombre *</Text>
            <Input value={form.nombre} onChange={set("nombre")} placeholder="Ej: Cuenta principal MP" />
          </div>
          {esAdmin && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>La administra</Text>
              <Select
                style={{ width: "100%" }}
                value={form.id_tesorero || 0}
                onChange={(v) => setForm((f) => ({ ...f, id_tesorero: v }))}
                options={[
                  { value: 0, label: "La tienda" },
                  ...tesoreros.map((t) => ({
                    value: t.id,
                    label: [t.nombre, t.apellido].filter(Boolean).join(" ") || t.usuario,
                  })),
                ]}
              />
              {tesoreros.length === 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Para asignarla a alguien, sumalo como tesorero en Colaboradores.
                </Text>
              )}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Entidad</Text>
              <Input value={form.entidad} onChange={set("entidad")} placeholder="Ej: Mercado Pago" />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Banco</Text>
              <Input value={form.banco} onChange={set("banco")} placeholder="Ej: Santander" />
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>CBU / CVU</Text>
            <Input value={form.cbu_cvu} onChange={set("cbu_cvu")} placeholder="22 dígitos" />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>Alias</Text>
            <Input value={form.alias} onChange={set("alias")} placeholder="Ej: mi.tienda.mp" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Titular</Text>
              <Input value={form.titular} onChange={set("titular")} placeholder="Nombre del titular" />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>CUIT</Text>
              <Input value={form.cuit} onChange={set("cuit")} placeholder="20-12345678-9" />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
